import { type VariantProps, cva } from "cva";

import { cn } from "@bidbattles/bloks/lib/ui";
import { useState } from "react";
import { Eye, EyeOff } from "untitledui-js/react";
import { Button } from "./button";

export const inputWrapper = cva({
	base: cn(
		"inline-flex w-full min-w-0 bg-input-background",
		"transition-all duration-100",
	),
	variants: {
		variant: {
			default:
				"border-b-[1px] border-gray-300 focus-within:border-b-accent-primary/100",
			box: "border-[1px] border-gray-300 rounded-sm shadow-xs shadow-gray-300/50 focus-within:shadow-accent-primary/50 focus-within:border-accent-primary",
			roundedBox:
				"border-[1px] border-white rounded-[2.5rem] shadow-xs shadow-gray-300/50 focus-within:shadow-accent-primary/50 focus-within:border-accent-primary",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

export const baseInput = cva({
	base: cn(
		"appearance-none px-2 py-2.5 inline-flex items-center grow w-full",
		"bg-transparent text-gray-900 placeholder:text-gray-300 placeholder:opacity-100 selection:bg-black selection:text-white bg-transparent text-sm md:text-base outline-none",
		"file:h-12 file:text-sm file:font-medium",
	),
	variants: {
		variant: {
			default: "",
			box: "",
			roundedBox: "",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

const attachmentVariants = cva({
	base: "inline-flex items-center justify-center px-2.5 py-2.5 text-gray-500",
	variants: {
		variant: {
			default: "",
			box: "",
			roundedBox: "",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

export type BaseInputVariantProps = VariantProps<typeof baseInput>;
export type InputProps = React.ComponentProps<"input"> &
	BaseInputVariantProps & {
		prefix?: React.ReactNode;
		suffix?: React.ReactNode;
	};

function Input({
	className,
	type: typeProp,
	variant,
	prefix,
	suffix: suffixProp,
	...props
}: InputProps) {
	const [type, setType] = useState(typeProp);

	let suffix = suffixProp;
	if (typeProp === "password") {
		suffix = (
			<Button
				variant="icon"
				size="icon-sm"
				className="text-gray-500 focus-visible:text-accent-primary transition-colors duration-100 focus-visible:outline-none"
				onClick={() => {
					setType((type) => (type === "password" ? "text" : "password"));
				}}
			>
				{type === "password" ? (
					<>
						<span className="sr-only">Show password</span>
						<Eye stroke="currentColor" size="1em" />
					</>
				) : (
					<>
						<span className="sr-only">Hide password</span>
						<EyeOff stroke="currentColor" size="1em" />
					</>
				)}
			</Button>
		);
	}

	return (
		<div className={cn(inputWrapper({ variant }), className)}>
			{!!prefix && (
				<div className={cn(attachmentVariants({ variant }))}>{prefix}</div>
			)}
			<input
				type={type}
				className={cn(baseInput({ variant }), "h-11")}
				{...props}
			/>
			{!!suffix && (
				<div className={cn(attachmentVariants({ variant }))}>{suffix}</div>
			)}
		</div>
	);
}

function TextArea({
	className,
	variant,
	...props
}: React.ComponentProps<"textarea"> & BaseInputVariantProps) {
	return (
		<div className={cn(inputWrapper({ variant }), className)}>
			<textarea className={cn(baseInput({ variant }), className)} {...props} />
		</div>
	);
}

export { Input, TextArea };
